import { formatNumber } from "@/v2-common/utils/utils.formatting"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import type { UsageProfileFragment } from "@/v2-backoffice-client/usage/__types__/usage.fragment"
import {
  APP_MIX_TOOLTIP,
  formatAppArea,
  getScoreLevel,
  SCORE_LEVEL_LABELS,
} from "@/v2-backoffice-client/usage/usage.constants"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageScoreMeter from "@/v2-backoffice-client/usage/UsageScoreMeter"
import UsageHistoryStrip from "@/v2-backoffice-client/usage/UsageHistoryStrip"
import UsageAppMix from "@/v2-backoffice-client/usage/UsageAppMix"
import UsageProductChips from "@/v2-backoffice-client/usage/UsageProductChips"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageReportProse from "@/v2-backoffice-client/usage/report/UsageReportProse"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import UsageLicenseDeniedSummary from "@/v2-backoffice-client/usage/denied/UsageLicenseDeniedSummary"
import {
  deriveDailyTotals,
  padDailyTotals,
  type UsageAreaDayRow,
} from "@/v2-backoffice-client/usage/usage.utils"
import { activeApps } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"
import { deriveUserStory } from "@/v2-backoffice-client/user/usage/user.usage.story"
import { deriveUserAppMix } from "@/v2-backoffice-client/user/usage/user.usage.utils"
import { USER_USAGE_HISTORY_DAYS } from "@/v2-backoffice-client/user/usage/user.usage.constants"

export type UserUsageOverviewProps = {
  userId: number
  subject: string
  profile: UsageProfileFragment
  /** Every product's rows for the history window. */
  areaDays: UsageAreaDayRow[]
  error?: Error
}

// The person across every product: the profile's four figures, the story
// in a paragraph, which products carry the work, the days with activity,
// and what they asked for and do not have.
function UserUsageOverview(props: UserUsageOverviewProps) {
  const { userId, subject, profile, areaDays, error } = props

  const appMix = deriveUserAppMix(profile)
  const story = deriveUserStory({ profile, appMix, subject })
  const history = padDailyTotals(
    deriveDailyTotals(areaDays),
    USER_USAGE_HISTORY_DAYS,
  )
  const activeDays = history.filter((entry) => entry.value > 0).length
  const scoreLevel = getScoreLevel(profile.EngagementScore)
  const trendPct = profile.WeightedTrendPct ?? profile.TrendPct
  const weightedActivity28d =
    profile.WeightedEvents28d != null
      ? Math.round(profile.WeightedEvents28d)
      : profile.Events28d

  return (
    <div className="usage-report">
      <UsageReportSection
        eyebrow="Overview"
        title={`How ${subject} uses Placepoint`}
        lead={
          <>
            Engagement, activity and breadth across every product, computed
            every night; last active{" "}
            <RelativeDate date={profile.LastActiveAt} />. The tabs next to this
            one go into each product.
          </>
        }
        aside={<UsageProductChips apps={activeApps(appMix)} />}
      >
        <ErrorMessage error={error} />

        <div className={usageKpiGridClass(4)}>
          <UsageReportKpi
            label="Engagement"
            value={
              <>
                {profile.EngagementScore ?? "—"}
                <span className="truncate text-sm font-normal text-muted-foreground">
                  {SCORE_LEVEL_LABELS[scoreLevel]}
                </span>
              </>
            }
            sub={[
              "out of 100: how much, how broadly, how recently, how steadily",
              profile.EngagementScore != null ? (
                <UsageScoreMeter key="meter" score={profile.EngagementScore} />
              ) : (
                "no score yet"
              ),
            ]}
          />
          <UsageReportKpi
            label="Activity"
            value={
              <>
                {formatNumber(weightedActivity28d)}
                <UsageTrendBadge trendPct={trendPct} />
              </>
            }
            sub="weighted actions in the last 28 days, against the four weeks before"
          />
          <UsageReportKpi
            label="Active days"
            value={
              <>
                {profile.ActiveDays28d}
                <span className="text-sm font-normal text-muted-foreground">
                  of 28
                </span>
              </>
            }
            sub={
              <>
                last active <RelativeDate date={profile.LastActiveAt} />
              </>
            }
          />
          <UsageReportKpi
            label="Breadth"
            value={
              <>
                {profile.DistinctAreas28d ?? 0}
                <span className="text-sm font-normal text-muted-foreground">
                  use cases
                </span>
              </>
            }
            sub={
              profile.TopAreas28d[0]
                ? `mostly ${formatAppArea(profile.TopAreas28d[0].Area)}`
                : "no use-case activity yet"
            }
          />
        </div>

        <div className="usage-report-grid usage-report-grid--two">
          <UsageReportProse title="In short" paragraphs={story} />
          <UsageReportCard
            title="Which products carry the work"
            caption={APP_MIX_TOOLTIP}
          >
            <UsageAppMix entries={appMix} />
          </UsageReportCard>
          <UsageReportCard
            title={`Day by day, last ${USER_USAGE_HISTORY_DAYS} days`}
            caption={`Every product together: ${formatNumber(activeDays)} ${activeDays === 1 ? "day" : "days"} with activity in the last ${USER_USAGE_HISTORY_DAYS}. Darker is a busier day.`}
            wide
          >
            <UsageHistoryStrip entries={history} />
          </UsageReportCard>
          {profile.AutomationSuspect && (
            <UsageReportProse
              title="Looks automated"
              tone="warning"
              wide
              paragraphs={[
                `${profile.SuspectHours28d ?? 0} hour${(profile.SuspectHours28d ?? 0) === 1 ? "" : "s"} in the last 28 days showed machine-like use: sustained rates beyond what a person clicks (peak ${formatNumber(profile.PeakEventsPerHour28d)} events an hour${
                  profile.MedianPeakGapSeconds != null
                    ? `, a steady ${profile.MedianPeakGapSeconds.toFixed(0)} s between actions`
                    : ""
                }). Worth an API-license conversation.`,
              ]}
            />
          )}
        </div>
      </UsageReportSection>

      <UsageLicenseDeniedSummary userId={userId} subject={subject} />
    </div>
  )
}

export default UserUsageOverview
